import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NavigationPageItemDto,
  TableDto,
  TableRowDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  AG_GRID_GROUP_AUTO_COLUMN_ID,
  DetailPageCreationModal,
  FullscreenWidgetModal,
  GoogleFontIcon,
} from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  activeCustomFilterColumnIds,
  fetchFilterCustomization,
  filterCustomizationQueryKey,
} from '../../api/portalGateway/tableFilterCustomization';
import { useTenantInfoData } from '../../api/portalGateway/tenantInfo';
import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useFullscreenInstanceIds } from '../../hooks/useFullscreenInstanceIds';
import { useStore } from '../../store';
import {
  useDraftCustomFilterColumnIds,
  useIsDashboardDraftMode,
} from '../../store/states/dashboardEditMode';
import {
  DEFAULT_FULLSCREEN_PAGE_SIZE,
  DEFAULT_GROUP_TABLE_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
} from '../../utils/constants';
import { getTableFilterInstanceId } from '../../utils/filterInstanceId';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';
import { invalidateTable } from '../../utils/queryCacheHelper';
import { BulkDeleteConfirmation } from '../bulkDeleteConfirmation/BulkDeleteConfirmation';
import { useBulkDeleteConfirmationProps } from '../bulkDeleteConfirmation/useBulkDeleteConfirmationProps';
import { CreateRowModal } from '../createRow/CreateRowModal';
import { useCreateRowModalProperties } from '../createRow/useCreateRowModalProperties';
import { DataIngestionModal } from '../dataIngestion/DataIngestionModal';
import { useDataIngestionModalProperties } from '../dataIngestion/useDataIngestionModalProperties';
import { DocUploadModal } from '../docUpload/DocUploadModal';
import { useDocUploadStore } from '../docUpload/store';
import { InfoIconModal } from '../infoicon/InfoIconModal';
import { InitFundOperationModal } from '../initFundOperation/InitFundOperationModal';
import { useInitFundOperationModalProperties } from '../initFundOperation/useInitFundOperationModalProperties';
import { CreateInvestmentLimitModal } from '../investmentLimit/CreateInvestmentLimitModal';
import { useCreateInvestmentLimitModalProperties } from '../investmentLimit/useCreateInvestmentLimitModalProperties';
import { ViewInvestmentLimitModal } from '../investmentLimit/ViewInvestmentLimitModal';
import { PageSidebarLayout } from '../page-sidebar/PageSidebarLayout';
import { TableCustomFilter } from '../tablecustomization/TableCustomFilter';
import { TableCustomization } from '../tablecustomization/TableCustomization';
import { UpdateTableRowModal } from '../updateTableRow/UpdateTableRowModal';
import { useUpdateTableRowModalProperties } from '../updateTableRow/useUpdateTableRowModalProperties';
import { useWorkflowModal } from '../workflowAction/useWorkflowModal';
import { WorkflowActionModal } from '../workflowAction/WorkflowActionModal';
import { ZipUploadModal } from '../zipUpload/ZipUploadModal';

import { TableContent, TableContentProps } from './TableContent';
import { useComparisonDetailPageModalProperties } from './useComparisonDetailPageModalProperties';
import { CustomColumnWidths, useCustomization } from './useCustomization';
import { useGroupOnlyCustomization } from './useGroupOnlyCustomization';

export type TableProps = Pick<
  WidgetDto,
  'allowOpenInFullscreen' | 'collapsible' | 'id' | 'title' | 'infoIcon'
> & {
  entity: TableDto;
  instanceId: string;
  dashboardId?: string | null;
  dashboardContentId?: string;
  contentSourcePageId?: string;
  savedPageFilterSelections?: TableContentProps['savedPageFilterSelections'];
  savedCustomTableFilterSelections?: TableContentProps['savedCustomTableFilterSelections'];
} & Pick<NavigationPageItemDto, 'pageId'>;

export const Table = ({
  allowOpenInFullscreen = false,
  collapsible = false,
  id: widgetId,
  infoIcon,
  title: widgetTitle,
  entity,
  pageId,
  instanceId,
  dashboardId = null,
  dashboardContentId,
  contentSourcePageId,
  savedPageFilterSelections,
  savedCustomTableFilterSelections,
}: TableProps) => {
  // Per-tile isolation on dashboards; falls back to content id on normal pages.
  const localWidgetId = dashboardContentId ?? widgetId;

  const [customColumnWidths, setCustomColumnWidths] =
    useState<CustomColumnWidths>({});

  const [resetPagination, setPerPage, roleId = NO_ROLE_ID] = useStore(
    useShallow((state) => [
      state.resetPagination,
      state.setPerPage,
      state.activeUserRole?.id,
    ])
  );
  const {
    data: { enabledFeatures },
  } = useTenantInfoData();
  const isGroupPaginationEnabled = enabledFeatures.includes('groupPagination');

  const {
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    customPageFilterStoreId,
    cloneFullscreenInstanceState,
  } = useFullscreenInstanceIds({
    pageFilterInstanceId: instanceId,
    widgetFilterInstanceId: localWidgetId,
  });

  const [infoIconOpened, { close: closeInfoIcon, open: openInfoIcon }] =
    useDisclosure();

  const [zipUploadOpened, { close: closeZipUpload, open: openZipUpload }] =
    useDisclosure();

  const resetDocUploadStore = useDocUploadStore((state) => state.reset);

  const [docUploadOpened, { close: closeDocUpload, open: openDocUpload }] =
    useDisclosure(false, { onOpen: resetDocUploadStore });

  const { updateTableRowModalProps, open: openUpdateTableRow } =
    useUpdateTableRowModalProperties(
      pageId,
      widgetId,
      entity.config,
      instanceId
    );

  const { initFundOperationModalProps, open: openInitFundOperation } =
    useInitFundOperationModalProperties(pageId, widgetId, entity.config);

  const { createRowModalProps, open: openCreateRowModal } =
    // @todo: if we need to support modals in fullscreen, this needs a switch: instanceId / fullscreenInstanceId
    useCreateRowModalProperties(pageId, widgetId, entity.config, instanceId);

  const { dataIngestionModalProps, open: openDataIngestionModal } =
    useDataIngestionModalProperties(
      pageId,
      widgetId,
      entity.config,
      instanceId
    );

  const {
    createInvestmentLimitModalProps,
    open: openCreateInvestmentLimitModal,
  } = useCreateInvestmentLimitModalProperties(
    widgetId,
    entity.config,
    instanceId
  );
  const [viewedInvestmentLimitRow, setViewedInvestmentLimitRow] = useState<
    TableRowDto['data'] | null
  >(null);
  const openInvestmentLimitRow = useCallback((row: TableRowDto['data']) => {
    setViewedInvestmentLimitRow(row);
  }, []);
  const closeInvestmentLimitRow = useCallback(() => {
    setViewedInvestmentLimitRow(null);
  }, []);

  const {
    workflowActionModalProps,
    open: openWorkflowModal,
    openReadonly: openReadonlyWorkflowModal,
  } = useWorkflowModal(pageId, widgetId, entity.config);

  const comparisonDetailPages =
    entity.config.comparisonDetailPageConfig?.comparisonDetailPages ?? [];
  const {
    comparisonDetailPageModalProps,
    open: openComparisonDetailPageModal,
  } = useComparisonDetailPageModalProperties({
    comparisonDetailPages,
    pageFilterInstanceId: instanceId,
    roleId,
  });

  const closePageSidebar = useStore((state) => state.closePageSidebar);

  const [fullscreenOpened, { close: closeFullscreen, open: openFullscreen }] =
    useDisclosure(false, {
      onClose: () => {
        closePageSidebar('fullscreen');
      },
      onOpen: () => {
        cloneFullscreenInstanceState();
        closePageSidebar('page');
      },
    });

  const [
    customizationOpened,
    { close: closeCustomization, open: openCustomization },
  ] = useDisclosure();

  const [
    customFilterOpened,
    { close: closeCustomFilter, open: openCustomFilter },
  ] = useDisclosure();

  const {
    open: openBulkDeleteConfirmation,
    props: bulkDeleteConfirmationProps,
  } = useBulkDeleteConfirmationProps();

  const customFilterColumns = useMemo(
    () =>
      entity.config.columns.filter(
        (columnConfig) => columnConfig.customFilter != null
      ),
    [entity.config.columns]
  );

  const draftCustomFilterColumnIds = useDraftCustomFilterColumnIds(
    dashboardId,
    dashboardContentId
  );
  const {
    data: storedCustomFilterData,
    isLoading: isFilterCustomizationLoading,
  } = useQuery({
    queryKey: filterCustomizationQueryKey('content', localWidgetId),
    queryFn: fetchFilterCustomization,
    enabled:
      customFilterColumns.length > 0 && draftCustomFilterColumnIds == null,
  });

  const activeCustomFilterIds = useMemo(
    () =>
      activeCustomFilterColumnIds(
        draftCustomFilterColumnIds,
        storedCustomFilterData?.data?.columns?.map((column) => column.columnId),
        customFilterColumns.map((column) => column.id)
      ),
    [
      customFilterColumns,
      draftCustomFilterColumnIds,
      storedCustomFilterData?.data?.columns,
    ]
  );

  const {
    customizedColumns: rawCustomizedColumns,
    customizationData,
    isCustomized,
    isCustomizable,
    isColumnInitiallyHidden,
  } = useCustomization(localWidgetId, entity.config, customColumnWidths);

  const isDashboardDraftMode = useIsDashboardDraftMode(dashboardId);

  const { customizedColumns, groupOnlyVisualizationData } =
    useGroupOnlyCustomization({
      customizationData,
      customizedColumns: rawCustomizedColumns,
      isCustomizable,
      widgetId: localWidgetId,
      enableGroupOnlyVisualization:
        entity.config.enableGroupOnlyVisualization === true &&
        !isDashboardDraftMode,
    });

  const isGroupedTable = useMemo(
    () => customizedColumns.some((column) => column.grouping != null),
    [customizedColumns]
  );

  const onColumnWidthChanged = useCallback(
    (columnWidth: CustomColumnWidths) => {
      setCustomColumnWidths({ ...customColumnWidths, ...columnWidth });
    },
    [customColumnWidths]
  );

  const defaultPageSize = useMemo(() => {
    if (isGroupedTable && !isGroupPaginationEnabled) {
      return DEFAULT_GROUP_TABLE_PAGE_SIZE;
    }

    return DEFAULT_PAGE_SIZE;
  }, [isGroupPaginationEnabled, isGroupedTable]);

  const defaultFullscreenPageSize = useMemo(() => {
    if (isGroupedTable) {
      if (isGroupPaginationEnabled) {
        return DEFAULT_PAGE_SIZE;
      }

      return DEFAULT_GROUP_TABLE_PAGE_SIZE;
    }

    return DEFAULT_FULLSCREEN_PAGE_SIZE;
  }, [isGroupPaginationEnabled, isGroupedTable]);

  const tableContentProps = useMemo<TableContentProps>(
    () => ({
      activeCustomFilterIds,
      collapsible,
      customFilterColumns,
      customizedColumns,
      customPageFilterStoreId,
      defaultPageSize,
      entity,
      groupColumnWidth: customColumnWidths[AG_GRID_GROUP_AUTO_COLUMN_ID]?.width,
      infoIcon,
      isColumnInitiallyHidden,
      isCustomizable,
      isCustomized,
      onBulkUpdateTableRowModalOpened: openUpdateTableRow,
      onComparisonDetailPageModalOpened: openComparisonDetailPageModal,
      onCreateRowModalOpened: openCreateRowModal,
      onDataIngestionModalOpened: openDataIngestionModal,
      onCreateInvestmentLimitModalOpened: openCreateInvestmentLimitModal,
      onInvestmentLimitRowOpened: openInvestmentLimitRow,
      onInitFundOperationModalOpened: openInitFundOperation,
      onModalOpened: openWorkflowModal,
      onReadonlyWorkflowModalOpened: openReadonlyWorkflowModal,
      openCustomFilter,
      openCustomization,
      openDeleteConfirmation: openBulkDeleteConfirmation,
      openDocUpload,
      openInfoIcon,
      openZipUpload,
      pageFilterInstanceId: instanceId,
      pageId,
      dashboardId,
      dashboardContentId,
      contentSourcePageId,
      isFilterCustomizationLoading,
      savedPageFilterSelections,
      savedCustomTableFilterSelections,
      title: widgetTitle,
      widgetFilterInstanceId: localWidgetId,
      widgetId,
      isGroupedTable,
      groupOnlyVisualizationData,
    }),
    [
      activeCustomFilterIds,
      collapsible,
      customColumnWidths,
      customFilterColumns,
      customizedColumns,
      customPageFilterStoreId,
      entity,
      infoIcon,
      instanceId,
      isColumnInitiallyHidden,
      isCustomizable,
      isCustomized,
      openBulkDeleteConfirmation,
      openComparisonDetailPageModal,
      openCreateRowModal,
      openDataIngestionModal,
      openCreateInvestmentLimitModal,
      openInvestmentLimitRow,
      openCustomFilter,
      openCustomization,
      openDocUpload,
      openInfoIcon,
      openInitFundOperation,
      openUpdateTableRow,
      openWorkflowModal,
      openReadonlyWorkflowModal,
      openZipUpload,
      pageId,
      dashboardId,
      dashboardContentId,
      contentSourcePageId,
      isFilterCustomizationLoading,
      savedPageFilterSelections,
      savedCustomTableFilterSelections,
      localWidgetId,
      widgetId,
      widgetTitle,
      isGroupedTable,
      groupOnlyVisualizationData,
      defaultPageSize,
    ]
  );

  useEffect(() => {
    if (fullscreenOpened) {
      resetPagination(widgetFullscreenInstanceStateId);
      setPerPage(
        fullscreenInstanceStateId,
        widgetFullscreenInstanceStateId,
        defaultFullscreenPageSize
      );
    }
  }, [
    defaultFullscreenPageSize,
    fullscreenInstanceStateId,
    fullscreenOpened,
    resetPagination,
    setPerPage,
    widgetFullscreenInstanceStateId,
  ]);

  return (
    <>
      {ContentRenderer({
        ...tableContentProps,
        enableScrollElementToTop: true,
        onColumnWidthChanged: onColumnWidthChanged,
        openFullscreen: allowOpenInFullscreen ? openFullscreen : undefined,
        isGroupedTable,
      })}
      {allowOpenInFullscreen &&
        FullscreenContentRenderer({
          ...tableContentProps,
          closeFullscreen,
          defaultPageSize: defaultFullscreenPageSize,
          fullscreenOpened,
          isFullScreenMode: true,
          pageFilterInstanceId: fullscreenInstanceStateId,
          widgetFilterInstanceId: widgetFullscreenInstanceStateId,
        })}
      {infoIcon && (
        <InfoIconModal
          onClose={closeInfoIcon}
          opened={infoIconOpened}
          headline={infoIcon?.headline}
          text={infoIcon?.text}
        />
      )}
      {entity.config.enableDocumentZipUpload && (
        <ZipUploadModal
          contentId={widgetId}
          onClose={closeZipUpload}
          onUploadSuccess={async () => {
            await invalidateTable(entity.config.cacheKey, pageId, widgetId);
          }}
          opened={zipUploadOpened}
        />
      )}
      {entity.config.uploadJsonSchema && docUploadOpened && (
        <DocUploadModal
          contentId={widgetId}
          tableFilterInstanceId={getTableFilterInstanceId(
            localWidgetId,
            instanceId
          )}
          onClose={closeDocUpload}
          jsonSchema={entity.config.uploadJsonSchema}
          onUploadSuccess={async () => {
            await invalidateTable(entity.config.cacheKey, pageId, widgetId);
          }}
          enableAIMetadataExtraction={entity.config.enableAIMetadataExtraction}
          enablePatternBasedRenaming={entity.config.enablePatternBasedRenaming}
          uploadFilenamePattern={entity.config.uploadFilenamePattern}
          fileNameMetadataExtractionConfig={
            entity.config.documentMetaDataExtraction
          }
        />
      )}
      {updateTableRowModalProps && entity.config.updateJsonSchema && (
        <UpdateTableRowModal
          {...updateTableRowModalProps}
          jsonSchema={entity.config.updateJsonSchema}
        />
      )}
      {initFundOperationModalProps &&
        entity.config.initFundOperationJsonSchema && (
          <InitFundOperationModal
            {...initFundOperationModalProps}
            jsonSchema={entity.config.initFundOperationJsonSchema}
          />
        )}
      {createRowModalProps && <CreateRowModal {...createRowModalProps} />}
      {comparisonDetailPageModalProps != null && (
        <DetailPageCreationModal
          {...comparisonDetailPageModalProps}
          listIcon={<GoogleFontIcon name="compare_arrows" />}
          size={pixelSizeHelperPortalUi(618)}
        />
      )}
      {dataIngestionModalProps && (
        <DataIngestionModal {...dataIngestionModalProps} />
      )}
      {createInvestmentLimitModalProps && (
        <CreateInvestmentLimitModal {...createInvestmentLimitModalProps} />
      )}
      {viewedInvestmentLimitRow != null && (
        <ViewInvestmentLimitModal
          contentId={widgetId}
          dataLevels={entity.config.createRowConfig?.dataLevels ?? []}
          onClose={closeInvestmentLimitRow}
          opened
          row={viewedInvestmentLimitRow}
        />
      )}
      {workflowActionModalProps && (
        <WorkflowActionModal {...workflowActionModalProps} />
      )}
      {isCustomizable && (
        <TableCustomization
          customizationData={customizationData ?? null}
          modalOpened={customizationOpened}
          onClose={closeCustomization}
          tableConfig={entity.config}
          widgetId={localWidgetId}
          isCustomized={isCustomized}
        />
      )}
      {customFilterColumns.length > 0 && (
        <TableCustomFilter
          activeCustomFilterIds={activeCustomFilterIds}
          customFilterColumns={customFilterColumns}
          modalOpened={customFilterOpened}
          onClose={closeCustomFilter}
          referenceType="content"
          reference={localWidgetId}
          filterInstanceIds={[
            getTableFilterInstanceId(localWidgetId, instanceId),
            getTableFilterInstanceId(
              widgetFullscreenInstanceStateId,
              fullscreenInstanceStateId
            ),
          ]}
        />
      )}
      <BulkDeleteConfirmation {...bulkDeleteConfirmationProps} />
    </>
  );
};

const ContentRenderer = (tableProps: TableContentProps) => {
  return <TableContent {...tableProps} />;
};

type FullscreenContentRendererProps = TableContentProps & {
  fullscreenOpened: boolean;
  closeFullscreen: () => void;
};

const FullscreenContentRenderer = ({
  fullscreenOpened,
  ...tableProps
}: FullscreenContentRendererProps) => {
  return (
    <FullscreenWidgetModal
      opened={fullscreenOpened}
      onClose={tableProps.closeFullscreen}
    >
      <PageSidebarLayout scope="fullscreen">
        <TableContent {...tableProps} />
      </PageSidebarLayout>
    </FullscreenWidgetModal>
  );
};
